import MetricEventTypes from "@Enums/MetricEventTypes";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { computeScanRateFromScanScore } from "@Shared/utilities";
import { isNumber } from "lodash-es";

const UNIT_INTERVAL_METRICS = new Set<MetricEventTypes | MetricFilterTypes>([
  MetricEventTypes.CRITICAL_SCAN,
  MetricEventTypes.SCAN_FREQUENCY,
  MetricFilterTypes.CRITICAL_SCAN,
  MetricFilterTypes.SCAN_FREQUENCY,
]);

const isScanFrequencyMetric = (metricType: MetricEventTypes | MetricFilterTypes): boolean => {
  return metricType === MetricEventTypes.SCAN_FREQUENCY || metricType === MetricFilterTypes.SCAN_FREQUENCY;
};

export const isUnitIntervalMetric = (metricType?: MetricEventTypes | MetricFilterTypes): boolean => {
  return Boolean(metricType && UNIT_INTERVAL_METRICS.has(metricType));
};

export const normalizeMetricValue = (metricType: MetricEventTypes | MetricFilterTypes, value?: number) => {
  if (!isNumber(value)) return undefined;
  if (isScanFrequencyMetric(metricType)) {
    return computeScanRateFromScanScore(value);
  }
  if (isUnitIntervalMetric(metricType) && value > 1) return value / 100;
  return value;
};

const BYB_IQ_MAX = 1000;

export const getProgressPercentage = (metricType: MetricEventTypes | MetricFilterTypes, value?: number) => {
  if (metricType === MetricFilterTypes.BYB_IQ) {
    if (!isNumber(value)) return undefined;
    return Math.max(0, Math.min((value / BYB_IQ_MAX) * 100, 100));
  }

  const normalizedValue = normalizeMetricValue(metricType, value);
  if (!isNumber(normalizedValue)) return undefined;

  const scaledValue = isUnitIntervalMetric(metricType) ? normalizedValue * 100 : normalizedValue;
  return Math.max(0, Math.min(scaledValue, 100));
};

export const getDisplayValue = (metricType: MetricEventTypes | MetricFilterTypes, value?: number) => {
  const normalizedValue = normalizeMetricValue(metricType, value);
  if (!isNumber(normalizedValue)) return null;

  if (isUnitIntervalMetric(metricType)) return Number(normalizedValue.toFixed(2));
  return normalizedValue;
};

export const formatMetricDisplayScore = (
  metricType: MetricEventTypes | MetricFilterTypes,
  value: number | null,
  decimalPlaces = 1
): string | null => {
  return value === null ? null : value.toFixed(isUnitIntervalMetric(metricType) ? 2 : decimalPlaces);
};
